import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { BookingService } from '../services/booking.service'
import { BookingDetails } from '../domain/BookingDetails'
import { Subject, takeUntil, mergeMap, switchMap, of } from 'rxjs'
import { BookingDocket, CoverMenuChoice } from '../domain/BookingDocket'
import { ProductLibrary } from '../domain/ProductLibrary'
import { ProductLibraryService } from '../services/product-library.service'
import { Menu } from '../domain/Consumable'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { MenuViewComponent } from './menu-view.component'
import { Venue } from '../domain/Venue'
import { VenueService } from '../services/venue.service'

@Component({
    selector: 'app-menu-choices-edit',
    templateUrl: './menu-choices-edit.component.html',
    styleUrls: ['./menu-choices-edit.component.sass'],
})
export class MenuChoicesEditComponent implements OnInit, OnDestroy {
    bookingDetails: BookingDetails | null = null
    venue: Venue | null = null
    partySizePluralMapping = {
        '=1': '1 person',
        other: '# people',
    }
    docket: BookingDocket | null = null
    productLibrary: ProductLibrary | null = null
    menu: Menu | null = null
    selectedViews: { productName: string, modifierNames: string | null }[][] = []
    partyIndexes: number[] = []
    private onDestroy$ = new Subject<boolean>()

    constructor(
        private route: ActivatedRoute,
        private bookingService: BookingService,
        private productLibraryService: ProductLibraryService,
        private venueService: VenueService,
        private modalService: NgbModal,
        private router: Router
    ) {
    }

    ngOnInit() {
        this.route.params.pipe(
            takeUntil(this.onDestroy$),
            mergeMap(params => this.bookingService.getBookingDetails(params['bookingId'])),
            switchMap(details => {
                this.bookingDetails = details
                this.docket = details.docket
                if (!details) {
                    return of(null)
                }
                return this.venueService.getWithId(details.venueId)
            }),
            switchMap(venue => {
                this.venue = venue
                if (!this.bookingDetails) {
                    return of(null)
                }
                return this.productLibraryService.getForVenueId(this.bookingDetails.venueId)
            })
        ).subscribe(productLibrary => {
            this.productLibrary = productLibrary
            this.menu = this.resolveMenu()
            this.recomputeAllSelectedConsumables()
        })
    }

    ngOnDestroy() {
        this.onDestroy$.next(true)
        this.onDestroy$.complete()
    }

    private resolveMenu(): Menu | null {
        if (!this.productLibrary || !this.docket) {
            return null
        }
        return this.productLibrary.getMenu(this.docket.menuId)
    }

    openMenuView() {
        if (!this.menu || !this.venue || !this.productLibrary) {
            return
        }
        const modalRef = this.modalService.open(MenuViewComponent, { size: 'xl', fullscreen: 'md', scrollable: true, windowClass: 'customer-menu-window', modalDialogClass: 'modal-fluid-until-md', ariaLabelledBy: 'customer-menu-title' })
        const component = modalRef.componentInstance as MenuViewComponent
        component.venue = this.venue
        component.menu = this.menu
        component.productLibrary = this.productLibrary
        component.docket = this.docket
    }

    openCoverEditor(index: number) {
        this.router.navigate([index], { relativeTo: this.route, state: { menuChoicesEntry: true } })
    }

    private recomputeAllSelectedConsumables() {
        if (!this.bookingDetails) {
            this.selectedViews = []
            return
        }
        const partySize = this.bookingDetails.partySize
        this.selectedViews = new Array(partySize).fill(0).map((_, i) => this.computeViewsFromIndex(i))
        this.partyIndexes = Array.from({ length: partySize }, (_, i) => i)
    }

    private computeViewsFromIndex(index: number): { productName: string, modifierNames: string | null }[] {
        if (!this.productLibrary || !this.menu || !this.docket || !this.bookingDetails) {
            return []
        }
        const existingChoices: CoverMenuChoice[] = this.docket.coverChoices || []
        const choice = existingChoices[index] || new CoverMenuChoice(null, null, [])
        const selections = choice.selections || []
        return selections.map(selection => {
            const menuItem = this.menu!.findItemForSelection(selection)
            const consumable = menuItem
                ? this.productLibrary!.consumables.find(c => c.id === menuItem!.consumableId)
                : this.productLibrary!.consumables.find(c => !!c.getSKUWithId(selection.productSkuId))
            const productName = consumable?.displayName || 'Item'
            const names = (selection.modifierSkuIds || [])
                .map(id => this.resolveModifierName(id))
                .filter((n): n is string => !!n)
            const modifierNames = names.length > 0 ? names.join(', ') : null
            return { productName, modifierNames }
        })
    }

    hasSelections(index: number): boolean {
        const selection = this.docket?.coverChoices?.[index]?.selections
        return !!selection && selection.length > 0
    }

    get progressCompleted(): number {
        if (!this.docket || !this.docket.coverChoices) { return 0 }
        return this.docket.coverChoices.filter(c => !!c && c.selections && c.selections.length > 0).length
    }

    get progressPercentage(): number {
        const total = this.bookingDetails?.partySize || 0
        return total === 0 ? 0 : (this.progressCompleted / total) * 100
    }

    private resolveModifierName(modifierSkuId: string): string | null {
        if (!this.productLibrary) { return null }
        for (const group of (this.productLibrary.modifierGroups || [])) {
            for (const mod of group.modifiers) {
                if (mod.skus.some(s => s.id === modifierSkuId)) {
                    return mod.displayName
                }
            }
        }
        return null
    }
}
